import React, { useState, useEffect, useRef, useCallback } from 'react';
import GanttChart from './GanttChart';
import WorkflowHistory from './WorkflowHistory';
import ReportV2 from './ReportV2';
import AvaliacaoEficacia from './AvaliacaoEficacia';
import EditableField from './EditableField';
import API_URL from '../config';
import { ncService } from '../services/api';
import { showSuccess, showError, showRecalcularContextoToast } from '../utils/toast';
import { formatDateTime } from '../utils/formatDateTime';
import './NCResult.css';

// Import sub-components
import {
  NCResultHeader,
  NCResultScoreSection,
  NCResultDataSection,
  NCResultAnalysisSection,
  NCResultActionPlan,
  NCResultEvidences
} from './NCResult/index';
import CascataIAModal from './NCResult/CascataIAModal';
import NCResultSupplierInvite from './NCResult/NCResultSupplierInvite';
import NCStatusStepper from './NCResult/NCStatusStepper';
import SlaClock from './NCResult/SlaClock';
import ConfirmModal from './ui/ConfirmModal';
import CopilotoPanel from './Copiloto/CopilotoPanel'; // #47/TD106
import Preparar8DModal from './Preparar8DModal';

// Helper: fetch com token JWT automático
function authFetch(url, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

const FINAL_STATUSES = ['aprovado', 'aprovada', 'reprovado', 'rejeitada', 'encerrada'];

// #163 — upsell de tier NO LUGAR de uma seção Investigação-only oculta no
// Registro (antes só havia o banner consolidado no topo). Mostra onde o
// conteúdo bloqueado apareceria + CTA contextual pra aprofundar. Sem botão
// de status final (NC fechada não vende upgrade).
function SecaoBloqueadaUpsell({ icone, titulo, descricao, onUpgrade, investigando }) {
  return (
    <section className="result-section" style={{ opacity: 0.92 }}>
      <h3 style={{ color: '#92400e' }}>
        <span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>{icone}</span>
        {titulo}
        <span style={{
          marginLeft: '8px', fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
          textTransform: 'uppercase', color: '#b45309', background: '#fef3c7',
          border: '1px solid #fcd34d', borderRadius: '4px', padding: '2px 6px', verticalAlign: 'middle',
        }}>Investigação</span>
      </h3>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '12px', flexWrap: 'wrap',
        padding: '12px 14px', border: '1px dashed #fcd34d', borderRadius: '8px', background: '#fffbeb',
      }}>
        <span style={{ color: '#78350f', fontSize: '13px' }}>{descricao}</span>
        <button
          onClick={onUpgrade}
          disabled={investigando}
          style={{
            padding: '6px 14px', border: '1.5px solid #d97706', borderRadius: '6px',
            background: 'transparent', color: '#b45309', fontWeight: 600, fontSize: '13px',
            cursor: investigando ? 'wait' : 'pointer', whiteSpace: 'nowrap',
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}
        >
          {investigando ? 'Reanalisando…' : (
            <>
              <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>biotech</span>
              Aprofundar para Investigação
            </>
          )}
        </button>
      </div>
    </section>
  );
}

// TD #48 (Sessão 24): gate do banner contextual "Recalcular com IA".
// Mesma flag REACT_APP_CASCATA_IA_HABILITADA usada antes pelo botão fixo.
// Lida em runtime (não const module-level) pra Jest poder alternar a flag
// entre tests sem precisar `jest.resetModules`.
function isCascataIAHabilitada() {
  return (process.env.REACT_APP_CASCATA_IA_HABILITADA || 'false').toLowerCase() === 'true';
}

function NCResult({ data, user, onRefresh, onUnsavedChangesChange, onReprocessar }) {
  const [currentStatus, setCurrentStatus] = useState('rascunho');
  const [showReportV2, setShowReportV2] = useState(false);
  const [arquivos, setArquivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [investigando, setInvestigando] = useState(false);
  const [revertendo, setRevertendo] = useState(false);
  const [reanalisando, setReanalisando] = useState(false); // #216
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingNC, setDeletingNC] = useState(false);
  const [deleteFileConfirm, setDeleteFileConfirm] = useState(null);

  // TD #48 (Sessão 24): state do modal cascata IA — abre via banner contextual
  // quando contexto foi editado após última análise.
  const [cascataModalOpen, setCascataModalOpen] = useState(false);
  // #47/TD106 — painel do Copiloto (chat read-only sobre a NC).
  const [copilotoOpen, setCopilotoOpen] = useState(false);
  // 8D modal — intercepts the 8D download to let user fill D1/D8 extras.
  const [preparar8DOpen, setPreparar8DOpen] = useState(false);

  // #161/#226 — gating de tier do upgrade Registro→Investigação (flag-aware,
  // vem do /auth/me). Defaults conservadores (libera) = comportamento atual
  // quando o sinal não está presente. O backend gateia /investigar (403);
  // aqui é só a UX (selo "grátis" / cadeado "Pro").
  const investigacaoLiberada = user?.investigacao_liberada !== false;
  const degustacaoDisponivel = !!user?.degustacao_disponivel;
  const bloqueioMotivo = user?.investigacao_bloqueio_motivo || null;

  // #47/TD106 — Copiloto read-only é perk Pro+. O sinal vem do /auth/me
  // (default true se ausente → graceful; o gate autoritativo é o 403 do backend).
  // false → o painel mostra upsell em vez do chat.
  const copilotoLiberado = user?.copiloto_liberado !== false;
  // Callbacks ESTÁVEIS (identidade fixa) — sem isso, o arrow inline mudava a
  // cada re-render do NCResult e re-disparava o efeito de carregar histórico do
  // painel, apagando o chat local (achado da revisão). useCallback fixa a raiz.
  const abrirCopiloto = useCallback(() => setCopilotoOpen(true), []);
  const fecharCopiloto = useCallback(() => setCopilotoOpen(false), []);

  // Ponte Fase 1 (#47) — o Mentor APONTA a seção; aqui fechamos o painel e rolamos o
  // editor até ela (com um destaque leve). O mentor segue READ-ONLY — quem corrige é o
  // analista, na própria seção. Editor e painel são co-located nesta tela.
  const handleJumpToSection = useCallback((secaoId) => {
    setCopilotoOpen(false);
    setTimeout(() => {
      const el = document.getElementById(secaoId);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const bgPrev = el.style.backgroundColor;
      const trPrev = el.style.transition;
      el.style.transition = 'background-color .35s ease';
      el.style.backgroundColor = '#fff7d6';
      setTimeout(() => {
        el.style.backgroundColor = bgPrev;
        setTimeout(() => { el.style.transition = trPrev; }, 400);
      }, 1200);
    }, 80); // deixa o overlay fechar antes de rolar
  }, []);

  const podeRecalcular = (
    currentStatus === 'rascunho' &&
    isCascataIAHabilitada() &&
    !!data?.contexto_editado_apos_analise
  );

  // TD #48 follow-up (Sessão 24): toast contextual quando flag vira true.
  // Banner permanente fica no topo da tela; usuário pode estar no meio
  // após salvar edição e não rolar pra ver. Toast aparece imediato no
  // canto superior direito com botão "Recalcular agora" inline.
  // Dispara só na TRANSIÇÃO false→true (não a cada render com flag=true).
  const prevContextoEditadoRef = useRef(!!data?.contexto_editado_apos_analise);
  useEffect(() => {
    const atual = !!data?.contexto_editado_apos_analise;
    if (atual && !prevContextoEditadoRef.current && podeRecalcular) {
      showRecalcularContextoToast(
        () => setCascataModalOpen(true),  // Recalcular agora → abre modal
        () => {}  // Mais tarde → banner permanente cobre
      );
    }
    prevContextoEditadoRef.current = atual;
  }, [data?.contexto_editado_apos_analise, podeRecalcular]);

  // Decisão 1 Fase 1A/1C — banner de reversão pós-cascata IA.
  // Snapshot pré-cascata fica embutido em analise_v4._snapshot_pre_cascata_ia.
  // Quando presente + status rascunho, banner aparece com botão "Reverter".
  const snapshotInfo = data?.analise_v4?._snapshot_pre_cascata_ia;
  const podeReverter = (
    !!snapshotInfo &&
    currentStatus === 'rascunho' &&
    typeof snapshotInfo === 'object' &&
    snapshotInfo.estado_anterior
  );

  const handleReverterCascata = async () => {
    if (!data?.id || revertendo) return;
    const confirmacao = window.confirm(
      'Tem certeza que deseja reverter para a versão anterior à cascata IA? ' +
      'O resultado atual será descartado e o snapshot consumido (irreversível).'
    );
    if (!confirmacao) return;
    setRevertendo(true);
    try {
      await ncService.restaurarVersaoAnterior(data.id);
      showSuccess('Versão anterior restaurada. Recarregando análise...');
      if (onRefresh) onRefresh();
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        showError('Snapshot já consumido ou inexistente.');
      } else if (status === 409) {
        showError('NC precisa estar em rascunho pra reverter.');
      } else {
        showError(`Erro ao reverter: ${err.response?.data?.detail || err.message}`);
      }
    } finally {
      setRevertendo(false);
    }
  };
  
  React.useEffect(() => {
    if (data && data.workflow_status) {
      setCurrentStatus(data.workflow_status);
    }
  }, [data]);

  React.useEffect(() => {
    if (data?.id) {
      fetchArquivos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id]);

  const fetchArquivos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const res = await fetch(`${API_URL}/api/v2/nc/${data.id}/relatorio-completo`, { headers });
      if (res.ok) {
        const report = await res.json();
        setArquivos(report.arquivos || []);
      }
    } catch (err) {
      console.error('Erro ao buscar arquivos:', err);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('evidencias', files[i]);
      }
      const res = await fetch(`${API_URL}/api/nc/nc/${data.id}/evidencias`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData,
      });
      if (res.ok) {
        fetchArquivos();
      }
    } catch (err) {
      console.error('Erro ao enviar arquivo:', err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteFile = (fileId) => {
    setDeleteFileConfirm(fileId);
  };

  const handleDeleteFileConfirmed = async () => {
    const fileId = deleteFileConfirm;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/nc/arquivo/${fileId}`, {
        method: 'DELETE',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (res.ok) {
        setArquivos(prev => prev.filter(a => a.id !== fileId));
      }
    } catch (err) {
      console.error('Erro ao excluir arquivo:', err);
    } finally {
      setDeleteFileConfirm(null);
    }
  };
  
  if (!data) return null;

  const {
    id,
    dados_extraidos,
    analise,
    plano_acao,
    gantt,
    relatorio_html,
    score_qualidade,
    alertas,
    versao_analise,
    modo_analise,
    avisos_consistencia,
  } = data;

  // Sessão 41 — tier Registro é o enxuto: não exibe 5W2H/cronograma (perks da
  // Investigação). Só renderização; a NC continua com os dados no banco.
  const isRegistro = (modo_analise || 'registro') === 'registro';

  // #47/TD106 — modo MENTOR (flag global do /auth/me, default OFF). Mentor =
  // explica/critica a análise EXISTENTE; só faz sentido nas Investigações (o
  // backend 403a tudo que NÃO é 'investigacao' com a flag ON). Quando o mentor
  // está ON fora de uma Investigação, escondemos o gatilho (senão abriria um
  // painel que 403aria). Flag OFF → comportamento idêntico ao de hoje (chat
  // genérico nos 2 modos). Espelhamos EXATAMENTE o predicado do backend
  // (`modo_analise === 'investigacao'`) p/ não divergir do gate em valor inesperado.
  const isInvestigacaoMode = (modo_analise || 'registro') === 'investigacao';
  const copilotoMentorFlag = user?.copiloto_mentor_habilitado === true;
  const copilotoModoMentor = copilotoMentorFlag && isInvestigacaoMode;
  const copilotoDisponivel = !copilotoMentorFlag || isInvestigacaoMode;

  // #216: análise vazia (falhou ou nunca rodou) — sem causa raiz E sem porquês,
  // no legado e no v4. Sinal conservador: qualquer conteúdo em qualquer shape
  // conta como análise existente (NC legada não acende).
  const causaLegada = typeof analise?.causa_raiz === 'string' ? analise.causa_raiz.trim() : '';
  const causaV4 = data?.analise_v4?.causa_raiz?.declaracao || '';
  const porquesQtd =
    (Array.isArray(analise?.cinco_porques) ? analise.cinco_porques.length : 0) +
    (Array.isArray(data?.analise_v4?.porques) ? data.analise_v4.porques.length : 0);
  const analiseVazia = !causaLegada && !causaV4 && porquesQtd === 0;

  const handleStatusChange = (newStatus) => {
    setCurrentStatus(newStatus);
    if (onRefresh) onRefresh();
  };

  // #216: saída do beco da análise vazia/falha — re-dispara a análise async
  // (mesma rota do retry do NCProcessing) e leva o usuário pra tela de
  // processamento. 409 = guard de concorrência do backend (task já ativa).
  const handleAnalisarComIA = async () => {
    if (reanalisando) return;
    setReanalisando(true);
    try {
      const resp = await authFetch(`${API_URL}/api/nc/${id}/reanalisar-async`, { method: 'POST' });
      const json = await resp.json().catch(() => ({}));
      if (resp.status === 409) {
        showError(json.detail || 'Já existe uma análise em andamento para esta NC.');
        return;
      }
      if (!resp.ok) throw new Error(json.detail || `HTTP ${resp.status}`);
      if (onReprocessar) {
        onReprocessar({ ncId: id, taskId: json.task_id, numeroNC: json.numero_nc || data.numero_nc });
      } else {
        showSuccess('Análise reiniciada. Recarregue a NC em instantes.');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      showError(`Não foi possível iniciar a análise: ${err.message}`);
    } finally {
      setReanalisando(false);
    }
  };

  const downloadReport = () => {
    // Sanitizar HTML antes do download para evitar XSS.
    //
    // FIX Sessão 23 — `WHOLE_DOCUMENT: true` necessário pra preservar
    // `<!DOCTYPE>`, `<html>`, `<head>` e `<style>`. Sem essa flag,
    // DOMPurify retorna só o conteúdo do `<body>`, strippando o head/style.
    // Resultado anterior: relatório baixado renderizava sem CSS — totalmente
    // sem forma. Mesmo com `html`/`head`/`body`/`style` em ALLOWED_TAGS, o
    // default do DOMPurify é tratar input como fragment (não documento
    // inteiro). Bug captado em prod por Francis na Sessão 22 (Decisão 1
    // Fase 1D). O HTML vem do backend `report_service.generate_report()` que
    // produz documento completo com DOCTYPE + head + CSS inline.
    let safeHtml = relatorio_html;
    try {
      const DOMPurify = require('dompurify');
      safeHtml = DOMPurify.sanitize(relatorio_html, {
        WHOLE_DOCUMENT: true,
        ALLOWED_TAGS: ['h1','h2','h3','h4','h5','h6','p','br','hr','div','span','table','thead','tbody','tr','th','td','ul','ol','li','strong','em','b','i','img','a','style','head','body','html','meta','title','section','header','footer'],
        ALLOWED_ATTR: ['class','style','src','alt','href','colspan','rowspan','width','height','charset','content','name','lang']
      });
    } catch (e) {
      // DOMPurify não disponível — usar HTML como está
      console.warn('DOMPurify não disponível para sanitização');
    }
    const blob = new Blob([safeHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // FIX(G1): nome do arquivo usa numero_nc real (ex: RNC-007/2026)
    const safeName = (data.numero_nc || `NC-${data.id}`).replace(/[^A-Za-z0-9_-]/g, '-');
    a.download = `relatorio_${safeName}.html`;
    a.click();
    showSuccess('Relatório HTML baixado com sucesso!');
  };

  // #240 — Relatório 8D (D1-D8). Diferente do "Relatório HTML": o HTML do 8D NÃO
  // vem na NC (é gerado por um endpoint próprio, autenticado), então busca via
  // authFetch e abre numa nova aba (com fallback de download se o popup for bloqueado).
  const download8D = async () => {
    try {
      const resp = await authFetch(`${API_URL}/api/nc/${id}/relatorio-8d`);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const html = await resp.text();
      let safeHtml = html;
      try {
        const DOMPurify = require('dompurify');
        safeHtml = DOMPurify.sanitize(html, {
          WHOLE_DOCUMENT: true,
          ALLOWED_TAGS: ['h1','h2','h3','h4','h5','h6','p','br','hr','div','span','table','thead','tbody','tr','th','td','ul','ol','li','strong','em','b','i','img','a','style','head','body','html','meta','title','section','header','footer'],
          ALLOWED_ATTR: ['class','style','src','alt','href','colspan','rowspan','width','height','charset','content','name','lang']
        });
      } catch (e) { /* DOMPurify ausente — usa como veio */ }
      const blob = new Blob([safeHtml], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank');
      if (!win) {
        const safeName = (data.numero_nc || `NC-${data.id}`).replace(/[^A-Za-z0-9_-]/g, '-');
        const a = document.createElement('a');
        a.href = url; a.download = `relatorio-8d_${safeName}.html`; a.click();
        showSuccess('Relatório 8D baixado com sucesso!');
      } else {
        showSuccess('Relatório 8D gerado! Verifique a nova aba.');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showError(`Não foi possível gerar o Relatório 8D: ${err.message}`);
    }
  };

  const handleGanttUpdate = async (updatedTasks) => {
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${id}/gantt`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          gantt_data: updatedTasks
        })
      });

      if (!response.ok) {
        throw new Error('Erro ao salvar Gantt');
      }

      console.log('Gantt atualizado com sucesso');
    } catch (error) {
      console.error('Erro ao atualizar Gantt:', error);
      showError('Erro ao salvar alterações do Gantt');
    }
  };

  const handleAnalysisSave = () => {
    if (onRefresh) onRefresh();
  };

  const handleManualInvestigar = async () => {
    const ok = window.confirm(
      'Aprofundar para Investigação irá reanalisar a NC com hipótese alternativa, ' +
      '5 porquês completos e ações preventivas. Tempo estimado: ~40s. Continuar?'
    );
    if (!ok) return;
    setInvestigando(true);
    try {
      const response = await authFetch(`${API_URL}/api/v2/nc/${id}/investigar`, {
        method: 'POST',
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.detail || `Erro ${response.status}`);
      }
      if (onRefresh) onRefresh();
    } catch (err) {
      showError(`Erro ao aprofundar para investigação: ${err.message}`);
    } finally {
      setInvestigando(false);
    }
  };

  // FIX(F4): exclusão de NC pela página de análise
  const handleDeleteNC = async () => {
    setDeletingNC(true);
    try {
      const response = await authFetch(`${API_URL}/api/v3/nc/${id}`, { method: 'DELETE' });
      if (response.ok) {
        window.location.reload();
      } else {
        const error = await response.json().catch(() => ({}));
        showError(`Erro ao excluir: ${error.detail || 'Verifique seu nível de acesso.'}`);
      }
    } catch (err) {
      console.error('Erro ao excluir NC:', err);
      showError('Erro de conexão ao tentar excluir.');
    } finally {
      setDeletingNC(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="nc-result-container">
      {/* Header */}
      <NCResultHeader
        ncId={id}
        numeroNC={data.numero_nc}
        modoAnalise={modo_analise || 'registro'}
        onDownloadReport={downloadReport}
        onDownload8D={() => setPreparar8DOpen(true)}
        onShowReportV2={() => setShowReportV2(true)}
        onOpenCopiloto={copilotoDisponivel ? abrirCopiloto : undefined}
        copilotoMentor={copilotoModoMentor}
        userRole={user?.role}
        onDelete={() => setShowDeleteConfirm(true)}
        analiseVazia={analiseVazia}
      />

      {/* #278 — stepper visual do ciclo de vida da NC (derivado do workflow_status) */}
      <NCStatusStepper currentStatus={currentStatus} />

      {/* #243 — relógio de SLA 8D (só aparece se o cliente tem sla_8d_habilitado) */}
      <SlaClock ncId={id} />

      {/* 8D modal — intercepts download to let user fill D1/D8 extras before generating */}
      <Preparar8DModal
        isOpen={preparar8DOpen}
        onClose={() => setPreparar8DOpen(false)}
        ncId={id}
        metadados8d={data?.analise_completa_v2?.metadados_8d || {}}
        onDownload={download8D}
      />

      <ConfirmModal
        open={showDeleteConfirm}
        title="Excluir Não Conformidade"
        icon="delete"
        danger
        loading={deletingNC}
        confirmLabel="Confirmar exclusão"
        onConfirm={handleDeleteNC}
        onCancel={() => setShowDeleteConfirm(false)}
      >
        <p>A NC <strong>{data.numero_nc || `NC-${id}`}</strong> será removida das listagens.</p>
        <p>Esta ação não pode ser desfeita.</p>
      </ConfirmModal>

      <ConfirmModal
        open={deleteFileConfirm !== null}
        title="Excluir arquivo"
        icon="delete"
        danger
        confirmLabel="Confirmar exclusão"
        onConfirm={handleDeleteFileConfirmed}
        onCancel={() => setDeleteFileConfirm(null)}
      >
        <p>O arquivo será removido permanentemente desta NC.</p>
      </ConfirmModal>

      {/* #216: análise vazia (falhou ou nunca rodou) — saída do beco. Aparece
          independente do workflow status: cobre NCs órfãs presas em em_analise
          por falhas anteriores ao fix do backend. */}
      {analiseVazia && !FINAL_STATUSES.includes(currentStatus) && (
        <div className="cascata-recalcular-banner" role="status" aria-live="polite">
          <div className="cascata-recalcular-info">
            <span className="material-symbols-outlined cascata-recalcular-icon" aria-hidden="true">science</span>
            <div>
              <strong>A análise de IA ainda não foi concluída para esta NC</strong>
              <div className="cascata-recalcular-meta">
                Ela pode ter falhado ou nunca ter sido executada. Rode a análise
                para gerar causa raiz, porquês e ações corretivas.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="cascata-recalcular-btn"
            onClick={handleAnalisarComIA}
            disabled={reanalisando}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>science</span>
            {reanalisando ? 'Iniciando…' : 'Analisar com IA'}
          </button>
        </div>
      )}

      {/* Decisão 1 Fase 1C — Banner de reversão pós-cascata IA.
          Aparece quando snapshot pré-cascata existe e NC está em rascunho.
          Permite reverter 1x via endpoint /restaurar-versao-anterior (Fase 1A). */}
      {podeReverter && (
        <div className="cascata-restore-banner" role="status" aria-live="polite">
          <div className="cascata-restore-info">
            <span className="material-symbols-outlined cascata-restore-icon" aria-hidden="true">undo</span>
            <div>
              <strong>Versão anterior recuperável</strong>
              <div className="cascata-restore-meta">
                {/* #153: rótulo derivado do motivo (cascata IA OU reanálise) —
                    o snapshot agora também é gravado nas reanálises. */}
                {({
                  cascata_ia: 'Cascata IA',
                  reanalise: 'Reanálise',
                  reanalise_async: 'Reanálise',
                  reanalise_6m: 'Reanálise do 6M',
                  upgrade_investigacao: 'Upgrade para Investigação',
                  editar_causa_raiz: 'Reanálise (mudança de causa raiz)',
                }[snapshotInfo.motivo] || 'Versão anterior salva')} em {formatDateTime(snapshotInfo.criado_em, user)}
                {snapshotInfo.criado_por && ` por ${snapshotInfo.criado_por}`}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="cascata-restore-btn"
            onClick={handleReverterCascata}
            disabled={revertendo}
          >
            {revertendo ? 'Revertendo...' : 'Reverter versão anterior'}
          </button>
        </div>
      )}

      {/* TD #48 (Sessão 24) — Banner contextual "Recalcular com IA".
          Aparece SÓ quando: NC em rascunho + feature flag ON + backend
          marcou contexto_editado_apos_analise=true (usuário editou
          descricao_nc/contexto_processo/contexto_setorial_custom/industria
          depois da última análise). Substitui o botão fixo que vivia no
          header da seção Análise (visível demais). UX educacional: sistema
          sugere a ação só quando faz sentido. */}
      {podeRecalcular && (
        <div className="cascata-recalcular-banner" role="status" aria-live="polite">
          <div className="cascata-recalcular-info">
            <span className="material-symbols-outlined cascata-recalcular-icon" aria-hidden="true">autorenew</span>
            <div>
              <strong>Contexto da NC foi editado após a última análise</strong>
              <div className="cascata-recalcular-meta">
                A IA pode regenerar a análise considerando o contexto atualizado.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="cascata-recalcular-btn"
            onClick={() => setCascataModalOpen(true)}
            title="Re-deriva toda a análise (6M, porquês, causa) a partir do contexto atual — suas edições à mão são preservadas e sinalizadas"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>autorenew</span> Recalcular com IA
          </button>
        </div>
      )}

      {/* Modal cascata IA — gatilho agora é o banner acima (TD #48). */}
      {isCascataIAHabilitada() && (
        <CascataIAModal
          ncId={id}
          isOpen={cascataModalOpen}
          onClose={() => setCascataModalOpen(false)}
          onCascataOk={() => { if (onRefresh) onRefresh(); }}
        />
      )}

      {/* #47/TD106 — Copiloto: chat read-only sobre a NC (perk Pro+). */}
      <CopilotoPanel
        ncId={id}
        ncLabel={data.numero_nc || `NC-${id}`}
        isOpen={copilotoOpen}
        onClose={fecharCopiloto}
        liberado={copilotoLiberado}
        mentor={copilotoModoMentor}
        streaming={user?.copiloto_streaming_habilitado === true}
        onJumpToSection={copilotoModoMentor ? handleJumpToSection : undefined}
      />

      {/* Upsell de tier — visível em modo Registro, sem status final. Lista o que a
          Investigação adiciona. Sessão 41: consolidado num ÚNICO banner confiável
          (antes dependia de 2 banners que podiam sumir por status/dismiss). */}
      {(modo_analise || 'registro') === 'registro'
        && !FINAL_STATUSES.includes(currentStatus) && (
        <div style={{
          margin: '12px 0',
          padding: '10px 14px',
          border: '1px solid #fcd34d',
          borderRadius: '8px',
          background: '#fffbeb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{ color: '#78350f', fontSize: '13px' }}>
            Quer um diagnóstico mais profundo? A análise de Investigação adiciona Ishikawa (6M), hipótese alternativa, ações preventivas, plano 5W2H detalhado, cronograma, verificação de eficácia e impacto potencial.
            {/* #226 — degustação: a 1ª Investigação é grátis. */}
            {investigacaoLiberada && degustacaoDisponivel && (
              <strong style={{ color: '#15803d', display: 'block', marginTop: '4px' }}>
                🎁 Sua primeira Investigação completa é por nossa conta.
              </strong>
            )}
            {/* #161 — esgotada/Starter: copy de upsell pro Pro (vem do backend). */}
            {!investigacaoLiberada && bloqueioMotivo && (
              <strong style={{ color: '#6d28d9', display: 'block', marginTop: '4px' }}>
                {bloqueioMotivo}
              </strong>
            )}
          </span>
          <button
            onClick={handleManualInvestigar}
            disabled={investigando || !investigacaoLiberada}
            title={!investigacaoLiberada ? (bloqueioMotivo || 'Disponível a partir do plano Pro') : undefined}
            style={{
              padding: '6px 14px',
              border: `1.5px solid ${!investigacaoLiberada ? '#a78bda' : '#d97706'}`,
              borderRadius: '6px',
              background: 'transparent',
              color: !investigacaoLiberada ? '#6d28d9' : '#b45309',
              fontWeight: 600,
              fontSize: '13px',
              cursor: investigando ? 'wait' : (!investigacaoLiberada ? 'not-allowed' : 'pointer'),
              opacity: !investigacaoLiberada ? 0.75 : 1,
              whiteSpace: 'nowrap',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {investigando ? 'Reanalisando…' : !investigacaoLiberada ? (
              <>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>lock</span>
                Disponível no Pro
              </>
            ) : (
              <>
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>biotech</span>
                Aprofundar para Investigação{degustacaoDisponivel ? ' · grátis' : ''}
              </>
            )}
          </button>
        </div>
      )}

      {/* Modal do Relatório v2.0 */}
      {showReportV2 && (
        <ReportV2 
          ncId={id} 
          onClose={() => setShowReportV2(false)} 
        />
      )}

      {/* Score e Alertas v2.0 */}
      <NCResultScoreSection
        scoreQualidade={score_qualidade}
        modoAnalise={modo_analise || 'registro'}
        avisosConsistencia={avisos_consistencia}
        versaoAnalise={versao_analise}
        alertas={alertas}
        ncId={id}
        currentStatus={currentStatus}
        user={user}
        onStatusChange={handleStatusChange}
        analiseVazia={analiseVazia}
      />

      {/* Sprint-2 hotfix: descrição editável (fonte: nc.descricao_nc coluna SQL) */}
      <section className="result-section">
        <h3><span className="material-symbols-outlined" style={{ fontSize: '18px', verticalAlign: 'middle', marginRight: '6px' }}>edit_note</span>Descrição da Não Conformidade</h3>
        <EditableField
          value={data?.descricao_nc || dados_extraidos?.descricao || ''}
          campo="descricao_nc"
          ncId={id}
          multiline
          placeholder="Descreva a não conformidade..."
          onUpdate={onRefresh}
        />
      </section>

      {/* Dados Extraídos */}
      <NCResultDataSection
        dadosExtraidos={dados_extraidos}
        nc={data}
        onUpdate={onRefresh}
      />

      {/* Análise */}
      <NCResultAnalysisSection
        analise={analise}
        analiseV4={data.analise_v4}
        currentStatus={currentStatus}
        ncId={id}
        nc={data}
        onSave={handleAnalysisSave}
        onUnsavedChangesChange={onUnsavedChangesChange}
      />

      {/* Plano 5W2H — Sessão 41: tier Registro oculta (perk da Investigação).
          #163: no Registro mostra o upsell NO LUGAR da seção (não some sem rastro). */}
      {!isRegistro ? (
        <NCResultActionPlan planoAcao={plano_acao} />
      ) : !FINAL_STATUSES.includes(currentStatus) && (
        <SecaoBloqueadaUpsell
          icone="checklist"
          titulo="Plano de Ação 5W2H"
          descricao="A Investigação detalha cada ação em 5W2H (o quê, por quê, quem, quando, onde, como e quanto) — pronto pro plano de ação formal."
          onUpgrade={handleManualInvestigar}
          investigando={investigando}
        />
      )}

      {/* Cronograma — Sessão 41: tier Registro oculta (perk da Investigação).
          #163: no Registro mostra o upsell NO LUGAR da seção. */}
      {!isRegistro ? (
        gantt && gantt.length > 0 && (
          <section className="result-section">
            <GanttChart tasks={gantt} onUpdate={handleGanttUpdate} />
          </section>
        )
      ) : !FINAL_STATUSES.includes(currentStatus) && (
        <SecaoBloqueadaUpsell
          icone="calendar_month"
          titulo="Cronograma das ações"
          descricao="A Investigação monta um cronograma (Gantt) das ações corretivas, com prazos e acompanhamento visual."
          onUpgrade={handleManualInvestigar}
          investigando={investigando}
        />
      )}

      {/* Avaliação de Eficácia */}
      <AvaliacaoEficacia ncId={id} />

      {/* Evidências Anexadas */}
      <NCResultEvidences
        ncId={id}
        currentStatus={currentStatus}
        arquivos={arquivos}
        onFileUpload={handleFileUpload}
        onDeleteFile={handleDeleteFile}
        uploading={uploading}
      />

      {/* #64 Feature 3 — convite a fornecedor (link público). Só em NC
          outbound (origem=fornecedor). Em NC interna/cliente o convite a
          fornecedor não faz sentido conceitual — esconde pra não poluir.
          (Decisão Francis pós-prod do outbound.) */}
      {data?.origem_tipo === 'fornecedor' && (
        <NCResultSupplierInvite ncId={id} user={user} nc={data} onPulled={onRefresh} />
      )}

      {/* Trilha de auditoria — ÚLTIMO elemento da tela (reposicionado a pedido:
          rodapé de auditoria, abaixo de Evidências). */}
      <section className="result-section">
        <WorkflowHistory ncId={id} />
      </section>
    </div>
  );
}

export default NCResult;